import axios from 'axios';
import {Form} from '@/plugins/FWForms/index';
import * as types from '../mutation-types';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {Connection} from '@/types';
import {push} from 'notivue';
import {handleApiError} from '@/utils/errorHandler.ts';


export interface TeslaConnection {
    active: boolean;
    connected: boolean;
    email: string;
    expires: string;
    loading: boolean;
    /** Tesla rejected the refresh token; the host must sign in again. */
    needs_reconnect?: boolean;
    photo_url: string;
    vehicles: any[];
    uuid?: string;
}

export interface TeslaState {
    init: boolean;
    loading: Record<string, boolean>;
    credentials: {
        email: string;
        password: string;
    };
    connections: TeslaConnection[];
    vehicles: any[];
    currentWorkingTeslaVehicle: any;
    currentWorkingTeslaVehicleDrivers: any[];
    vehicleInvitations: any[];
    chargingHistory: any[];
    chargingHistoryPaginate: any;
    chargingInvoices: any[];
    invoices: any[];
    invoices_meta: any;
    invoices_pdf: string;
    currentWorkingTeslaVehicleAppAccess: any[];
    currentWorkingTeslaVehicleState: any;
    currentWorkingTeslaVehicleData: any;
    currentWorkingTeslaVehicleAsleep: boolean;
    /** Why the last read failed: Tesla's real state (asleep / offline) and a message for the host. */
    currentWorkingTeslaVehicleUnavailable: { state: string, message: string | null } | null;
    form: Form<object>;
}

const state: TeslaState = {
    init: false,

    loading: {
        login: false,
        delete: false,
        toggle: false,
        refresh_invoice: false,
        invoices: false,
        invoicePDF: false,
        refreshVehicles: false,
        appAccess: false,
        vehicleState: false,
        vehicleData: false,
        wakeVehicle: false,
        chargingHistory: false,
        invitations: false,
    },

    credentials: {
        email: '',
        password: '',
    },

    connections: [],
    vehicles: [],
    currentWorkingTeslaVehicle: {},
    currentWorkingTeslaVehicleDrivers: [],
    vehicleInvitations: [],
    chargingHistory: [],
    chargingHistoryPaginate: null,
    chargingInvoices: [],
    invoices: [],
    invoices_meta: {},
    invoices_pdf: '',
    currentWorkingTeslaVehicleAppAccess: [],
    currentWorkingTeslaVehicleState: {},
    currentWorkingTeslaVehicleData: {},
    currentWorkingTeslaVehicleAsleep: false,
    currentWorkingTeslaVehicleUnavailable: null,
    form: new Form(),
};

const getters = {
    loading: (state: TeslaState) => state.loading,
    credentials: (state: TeslaState) => state.credentials,
    connections: (state: TeslaState) => state.connections,
    vehicles: (state: TeslaState) => state.vehicles,
    currentWorkingTeslaVehicle: (state: TeslaState) => state.currentWorkingTeslaVehicle,
    currentWorkingTeslaVehicleDrivers: (state: TeslaState) => state.currentWorkingTeslaVehicleDrivers,
    vehicleInvitations: (state: TeslaState) => state.vehicleInvitations,
    chargingHistory: (state: TeslaState) => state.chargingHistory,
    chargingHistoryPaginate: (state: TeslaState) => state.chargingHistoryPaginate,
    currentWorkingTeslaVehicleAppAccess: (state: TeslaState) => state.currentWorkingTeslaVehicleAppAccess,
    currentWorkingTeslaVehicleState: (state: TeslaState) => state.currentWorkingTeslaVehicleState,
    currentWorkingTeslaVehicleData: (state: TeslaState) => state.currentWorkingTeslaVehicleData,
    currentWorkingTeslaVehicleAsleep: (state: TeslaState) => state.currentWorkingTeslaVehicleAsleep,
    currentWorkingTeslaVehicleUnavailable: (state: TeslaState) => state.currentWorkingTeslaVehicleUnavailable,
    chargingInvoices: (state: TeslaState) => state.chargingInvoices,
    invoices: (state: TeslaState) => state.invoices,
    invoicesMeta: (state: TeslaState) => state.invoices_meta,
    invoicesPDF: (state: TeslaState) => state.invoices_pdf,
    form: (state: TeslaState) => state.form,
};

const mutations = {
    [types.SET_TESLA_INIT](state: TeslaState, init: boolean) {
        state.init = init;
    },
    [types.CLEAR_TESLA_FORM_ERRORS](state: TeslaState, form_field) {
        if (form_field === null || form_field === undefined) {
            state.form.errors.clear();
        } else {
            state.form.errors.clear(form_field);
        }
    },
    [types.SET_TESLA_LOADING](state: TeslaState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_TESLA_CREDENTIALS](state: TeslaState, init) {
        state.init = init;
    },
    [types.SET_TESLA_CONNECTIONS](state: TeslaState, connections) {
        state.connections = connections;
    },
    [types.UPDATE_TESLA_CONNECTION](state: TeslaState, tesla) {
        if (tesla) {
            const connIndex = state.connections.findIndex((c) => tesla.uuid === c.uuid);
            if (connIndex >= 0) {
                state.connections[connIndex] = tesla;
                if (!tesla?.active) {
                    state.connections[connIndex].connected = false;
                }
            } else {
                state.connections.push(tesla);
            }
        }
    },
    [types.SET_TESLA_VEHICLES](state: TeslaState, vehicles) {
        state.vehicles = vehicles;
    },
    [types.SET_CURRENT_WORKING_TESLA_VEHICLE](state: TeslaState, vehicle) {
        state.currentWorkingTeslaVehicle = vehicle;
    },
    [types.SET_CURRENT_WORKING_TESLA_VEHICLE_DRIVERS](state: TeslaState, drivers) {
        state.currentWorkingTeslaVehicleDrivers = drivers;
    },
    [types.SET_CURRENT_WORKING_TESLA_VEHICLE_HISTORY](state: TeslaState, chargingHistory) {
        state.chargingHistory = chargingHistory;
    },
    [types.SET_CURRENT_WORKING_TESLA_VEHICLE_HISTORY_PAGINATE](state: TeslaState, paginate) {
        state.chargingHistoryPaginate = paginate;
    },
    [types.SET_TESLA_VEHICLE_INVITATIONS](state: TeslaState, invites) {
        state.vehicleInvitations = invites;
    },
    [types.SET_TESLA_CHARGING_INVOICES](state: TeslaState, invoices) {
        if (invoices && invoices.length > 0) {
            state.chargingInvoices = invoices;
        }
    },
    [types.SET_TESLA_INVOICES](state: TeslaState, invoices) {
        state.invoices = invoices;
    },
    [types.SET_TESLA_INVOICES_META](state: TeslaState, meta) {
        state.invoices_meta = meta;
    },
    [types.SET_TESLA_INVOICE_PDF](state: TeslaState, pdf) {
        state.invoices_pdf = pdf;
    },
    SET_CURRENT_WORKING_TESLA_UNAVAILABLE(state: TeslaState, unavailable: {
        state: string,
        message: string | null
    } | null) {
        state.currentWorkingTeslaVehicleUnavailable = unavailable;
    },
    [types.SET_CURRENT_WORKING_TESLA_ASLEEP](state: TeslaState, asleep) {
        state.currentWorkingTeslaVehicleAsleep = asleep;
    },
    [types.SET_CURRENT_WORKING_TESLA_VEHICLE_APP_ACCESS](state: TeslaState, emails) {
        state.currentWorkingTeslaVehicleAppAccess = emails;
    },
    [types.SET_CURRENT_WORKING_TESLA_VEHICLE_STATE](state: TeslaState, vehicleState) {
        state.currentWorkingTeslaVehicleState = vehicleState;
    },
    [types.SET_CURRENT_WORKING_TESLA_VEHICLE_DATA](state: TeslaState, vehicleData) {
        state.currentWorkingTeslaVehicleData = vehicleData;
    },
};

const actions = {

    /**
     * Toggle active a Tesla account
     */
    async toggleActive({commit}, tesla: TeslaConnection): Promise<{ tesla: any } | undefined> {
        commit(types.SET_TESLA_LOADING, {subject: 'toggle', loading: true});

        try {
            const response = await axios.put(`/v1/connections/tesla/${tesla.uuid}/toggle`);
            commit('UPDATE_TESLA_CONNECTION', response?.data?.tesla);

            return {tesla: response?.data?.tesla ?? []};
        } catch {
            return undefined;
        } finally {
            commit(types.SET_TESLA_LOADING, {subject: 'toggle', loading: false});
        }
    },

    refreshToken({commit}, tesla: TeslaConnection) {
        commit(types.SET_TESLA_LOADING, {subject: 'toggle', loading: true});
        axios.post(`/v1/connections/tesla/${tesla.uuid}/refresh-token`)
            .then(() => {
                push.success({
                    message: 'Tesla token refreshed successful!',
                });
            })
            .catch(() => {
            })
            .finally(() => {
                commit(types.SET_TESLA_LOADING, {subject: 'toggle', loading: false});
            });
    },

    async deleteConnection({commit}, tesla: TeslaConnection): Promise<boolean> {
        commit(types.SET_TESLA_LOADING, {subject: 'delete', loading: true});

        try {
            await axios.delete(`/v1/connections/tesla/${tesla.uuid}`);
            push.success({
                message: 'Tesla connection removed successfully',
            });

            return true;
        } catch {
            return false;
        } finally {
            commit(types.SET_TESLA_LOADING, {subject: 'delete', loading: false});
        }
    },

    fetchUserInfo({commit}, tesla: TeslaConnection) {
        commit(types.SET_TESLA_LOADING, {subject: 'delete', loading: true});
        axios.get(`/v1/connections/tesla/${tesla.uuid}/user-info`)
            .then(() => {
                push.success({
                    message: 'Tesla connection success.',
                });
            })
            .catch(() => {
            })
            .finally(() => {
                commit(types.SET_TESLA_LOADING, {subject: 'delete', loading: false});
            });
    },

    getVehicles({state, commit}, tesla: TeslaConnection) {
        commit(types.SET_TESLA_LOADING, {subject: 'login', loading: true});

        const connIndex = state.connections.findIndex((c: Connection) => tesla.uuid === c.uuid);
        state.connections[connIndex].loading = true;
        state.connections[connIndex].connected = false;

        axios.get(`/v1/connections/tesla/${tesla.uuid}/vehicles`)
            .then((response) => {
                commit('SET_TESLA_CONNECTIONS', response?.data?.vehicles, []);
                state.connections[connIndex].connected = true;
            })
            .catch(() => {
            })
            .finally(() => {
                commit(types.SET_TESLA_LOADING, {subject: 'login', loading: false});
                state.connections[connIndex].loading = false;
            });
    },

    getVehicle({commit}, uuid: string) {
        commit(types.SET_TESLA_LOADING, {subject: 'login', loading: true});

        axios.get(`/v1/connections/tesla/vehicle/${uuid}`)
            .then((response) => {
                commit('SET_CURRENT_WORKING_TESLA_VEHICLE', response?.data?.vehicle, []);
            })
            .catch(() => {
            })
            .finally(() => {
                commit(types.SET_TESLA_LOADING, {subject: 'login', loading: false});
            });
    },

    fetchVehicleDrivers({commit}, uuid: string) {
        commit(types.SET_TESLA_LOADING, {subject: 'login', loading: true});

        axios.get(`/v1/connections/tesla/vehicle/${uuid}/drivers`)
            .then((response) => {
                commit('SET_CURRENT_WORKING_TESLA_VEHICLE_DRIVERS', response?.data?.drivers, []);
            })
            .catch(() => {
            })
            .finally(() => {
                commit(types.SET_TESLA_LOADING, {subject: 'login', loading: false});
            });
    },

    async createVehicleInvitation({commit}, uuid: string): Promise<{ invitation: any } | undefined> {
        commit(types.SET_TESLA_LOADING, {subject: 'invitations', loading: true});

        try {
            const response = await axios.post(`/v1/connections/tesla/vehicle/${uuid}/invitations`);

            return {invitation: response?.data?.invitation};
        } catch (e) {
            push.error({
                message: (axios.isAxiosError(e) ? (e.response?.data as { message?: string })?.message : undefined)
                    || 'Tesla could not create the key link.',
            });

            return undefined;
        } finally {
            commit(types.SET_TESLA_LOADING, {subject: 'login', loading: false});
        }
    },

    async fetchVehicleChargingHistory({commit}, data) {
        try {
            const responseData = await performApiRequest(commit, 'SET_TESLA_LOADING', 'chargingHistory', () => axios.get(`/v1/connections/tesla/vehicle/${data.tesla_uuid}/charging-history?page=${data.page}`));

            commit('SET_CURRENT_WORKING_TESLA_VEHICLE_HISTORY_PAGINATE', responseData.paginate, []);
            commit('SET_CURRENT_WORKING_TESLA_VEHICLE_HISTORY', responseData.data, []);
        } catch (error) {
            handleApiError(error);
        }
    },
    /** Base64 PDF for one invoice on a vehicle's charging session. */
    async fetchTeslaInvoice({commit}, data: { tesla_uuid: string, contentId: string }): Promise<{
        pdf?: string
    } | undefined> {
        commit(types.SET_TESLA_LOADING, {subject: 'invoicePDF', loading: true});

        try {
            const response = await axios.get(`/v1/connections/tesla/vehicle/${data.tesla_uuid}/charging-history-invoice`, {params: {contentId: data.contentId}});

            return response.data;
        } catch (e) {
            push.error({
                message: (axios.isAxiosError(e) ? (e.response?.data as { message?: string })?.message : undefined)
                    || 'Could not load that invoice.',
            });

            return undefined;
        } finally {
            commit(types.SET_TESLA_LOADING, {subject: 'invoicePDF', loading: false});
        }
    },
    async fetchVehicleInvitations({commit}, uuid: string) {
        try {
            const responseData = await performApiRequest(commit, 'SET_TESLA_LOADING', 'invitations', () => axios.get(`/v1/connections/tesla/vehicle/${uuid}/invitations`));

            commit('SET_TESLA_VEHICLE_INVITATIONS', responseData.invitations, []);
        } catch (error) {
            handleApiError(error);
        }
    },

    /**
     * Cancel vehicle invitation
     *
     */
    async cancelVehicleInvitation({commit}, data: { tesla_uuid: string, invite_id: string }) {
        try {
            await performApiRequest(commit, 'SET_TESLA_LOADING', 'invitations', () => axios.delete(`/v1/connections/tesla/vehicle/${data.tesla_uuid}/invitations/${data.invite_id}`));
        } catch (error) {
            handleApiError(error);
        }
    },

    refreshVehicles({state, commit}, tesla: TeslaConnection) {
        commit(types.SET_TESLA_LOADING, {subject: 'refreshVehicles', loading: true});

        const connIndex = state.connections.findIndex((c: Connection) => tesla.uuid === c.uuid);
        state.connections[connIndex].loading = true;
        state.connections[connIndex].connected = false;

        axios.post(`/v1/connections/tesla/${tesla.uuid}/refresh-vehicles`)
            .then((response) => {
                commit('SET_TESLA_CONNECTIONS', response?.data?.vehicles, []);
                state.connections[connIndex].connected = true;
            })
            .catch(() => {
            })
            .finally(() => {
                commit(types.SET_TESLA_LOADING, {subject: 'refreshVehicles', loading: false});
                state.connections[connIndex].loading = false;
            });
    },

    async wakeVehicle({commit}, tesla: TeslaConnection): Promise<{ waking?: any, woke?: TeslaConnection } | undefined> {
        commit(types.SET_TESLA_LOADING, {subject: 'wakeVehicle', loading: true});
        commit('SET_CURRENT_WORKING_TESLA_VEHICLE', tesla);

        try {
            const response = await axios.post(`/v1/connections/tesla/vehicles/${tesla.uuid}/wake`);

            if (response?.data?.wake?.state === 'asleep') {
                push.success({
                    message: 'Tesla is waking. It may take a few seconds.',
                });

                return {waking: response?.data?.wake};
            }

            commit('SET_CURRENT_WORKING_TESLA_ASLEEP', false);

            return {woke: tesla};
        } catch (e) {
            push.error({
                message: (axios.isAxiosError(e) ? (e.response?.data as {
                    message?: string
                })?.message : undefined) || 'There was an error',
            });

            return undefined;
        } finally {
            commit(types.SET_TESLA_LOADING, {subject: 'wakeVehicle', loading: false});
        }
    },

    getVehicleState({commit}, tesla: TeslaConnection) {
        commit(types.SET_TESLA_LOADING, {subject: 'vehicleState', loading: true});
        axios.get(`/v1/connections/tesla/vehicles/${tesla.uuid}/state`)
            .then((response) => {
                commit('SET_CURRENT_WORKING_TESLA_VEHICLE_STATE', response?.data?.state, []);
            })
            .catch((e) => {
                push.error({
                    message: (axios.isAxiosError(e) ? (e.response?.data as {
                        message?: string
                    })?.message : undefined) || 'There was an error',
                });
            }).finally(() => {
            commit(types.SET_TESLA_LOADING, {subject: 'vehicleState', loading: false});
        });
    },

    async getVehicleData({commit}, tesla: TeslaConnection): Promise<{
        data?: any,
        asleep?: TeslaConnection
    } | undefined> {
        commit(types.SET_TESLA_LOADING, {subject: 'vehicleData', loading: true});

        try {
            const response = await axios.get(`/v1/connections/tesla/vehicles/${tesla.uuid}/data`);
            commit('SET_CURRENT_WORKING_TESLA_ASLEEP', false);
            commit('SET_CURRENT_WORKING_TESLA_UNAVAILABLE', null);
            commit('SET_CURRENT_WORKING_TESLA_VEHICLE_DATA', response?.data?.data, []);

            return {data: response?.data?.data};
        } catch (e) {
            const asleep = axios.isAxiosError(e) && e.response?.status === 406;

            if (asleep) {
                // Not an error: the page shows the last saved reading and why there's no live one.
                const body = axios.isAxiosError(e) ? (e.response?.data as {
                    state?: string,
                    message?: string
                }) : undefined;
                commit('SET_CURRENT_WORKING_TESLA_ASLEEP', true);
                commit('SET_CURRENT_WORKING_TESLA_UNAVAILABLE', {
                    state: body?.state ?? 'asleep',
                    message: body?.message ?? null
                });

                return {asleep: tesla};
            }
            push.error({
                message: (axios.isAxiosError(e) ? (e.response?.data as {
                    message?: string
                })?.message : undefined) || 'There was an error',
            });

            return asleep ? {asleep: tesla} : undefined;
        } finally {
            commit(types.SET_TESLA_LOADING, {subject: 'vehicleData', loading: false});
        }
    },

    /** "Refresh invoices": the Fleet API sync (the scraper's payment-history route is gone). */
    checkInvoices({dispatch}, tesla: TeslaConnection) {
        return dispatch('syncChargingHistory', tesla);
    },

    chargingHistory({dispatch}, tesla: TeslaConnection) {
        return dispatch('syncChargingHistory', tesla);
    },


    getChargingInvoiceHistory({commit}, tesla: TeslaConnection) {
        commit(types.SET_TESLA_LOADING, {subject: 'invoices', loading: true});

        axios.get(`/v1/connections/tesla/${tesla.uuid}/charging-invoice-history`)
            .then((response) => {
                commit('SET_TESLA_CHARGING_INVOICES', response?.data?.charging);
            })
            .catch((e) => {
                push.error({
                    message: (axios.isAxiosError(e) ? (e.response?.data as {
                        message?: string
                    })?.message : undefined) || 'There was an error',
                });
            }).finally(() => {
            commit(types.SET_TESLA_LOADING, {subject: 'invoices', loading: false});
        });
    },

    /**
     * Pulls the account's Supercharger history from Tesla (billed per 50 sessions)
     * and returns the saved list. Resolves true on success.
     */
    async syncChargingHistory({commit}, tesla: TeslaConnection): Promise<boolean> {
        commit(types.SET_TESLA_LOADING, {subject: 'syncCharging', loading: true});

        try {
            const response = await axios.post(`/v1/connections/tesla/${tesla.uuid}/charging-history/sync`);
            commit('SET_TESLA_CHARGING_INVOICES', response?.data?.charging);
            commit('SET_TESLA_INVOICES_META', {created_at: new Date().toISOString()});
            push.success({message: 'Supercharger history is up to date.'});

            return true;
        } catch (e) {
            push.error({
                message: (axios.isAxiosError(e) ? (e.response?.data as { message?: string })?.message : undefined)
                    || 'Could not reach Tesla. Try again in a minute.',
            });

            return false;
        } finally {
            commit(types.SET_TESLA_LOADING, {subject: 'syncCharging', loading: false});
        }
    },

    getInvoices({dispatch}, tesla: TeslaConnection) {
        return dispatch('getChargingInvoiceHistory', tesla);
    },


    /**
     * Get invoice pdfs
     */
    /** item: a saved charging session row (tesla_charging.id). */
    getInvoicePDF({commit}, item: { id: number, contentId?: string }) {
        commit(types.SET_TESLA_LOADING, {subject: 'invoicePDF', loading: true});
        commit('SET_TESLA_INVOICE_PDF', null);

        return axios.get(`/v1/connections/tesla/charging/${item.id}/invoice`, {params: item.contentId ? {contentId: item.contentId} : {}})
            .then((response) => {
                commit('SET_TESLA_INVOICE_PDF', response?.data?.pdf);
            })
            .catch((e) => {
                push.error({
                    message: (axios.isAxiosError(e) ? (e.response?.data as {
                        message?: string
                    })?.message : undefined) || 'Could not load that invoice.',
                });
            }).finally(() => {
                commit(types.SET_TESLA_LOADING, {subject: 'invoicePDF', loading: false});
            });
    },

    async importTeslaVehiclesAsListings({commit}, {vehicle_uuids, tesla_uuid}) {
        try {
            const responseData = await performApiRequest(commit, 'SET_TESLA_LOADING', 'invoicePDF', () => axios.post(`/v1/connections/tesla/${tesla_uuid}/import`, {vehicle_uuids}));
            commit('listing/SET_ALL_LISTINGS', responseData.listings, {root: true});
            push.success({
                message: 'Tesla imported as listing successfully',
            });

            return true;
        } catch (error) {
            handleApiError(error);

            return false;
        }
    },

};


export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
